import './index.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import http from '../../../api/http';
import RecordSection from '../../view/RecordSection';
import StatusBadge from '../../ui/StatusBadge';

const messageOf = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

// Every type can be added from either end: canonical ("blocks") or inverse ("blocked by").
const relationOptions = (types, section) => types.flatMap((type) => {
  const options = [];
  if (!type.from || type.from.includes(section)) {
    options.push({ value: type.type, label: type.label || humanize(type.type), targets: type.to || null });
  }
  if (type.inverse && !type.symmetric && (!type.to || type.to.includes(section))) {
    options.push({ value: type.inverse, label: humanize(type.inverse), targets: type.from || null });
  }
  return options;
});

const RecordRef = ({ record }) => {
  if (!record) return null;
  const label = record.label || `${record.section} ${record.id}`;
  return (
    <span className="record-links-ref">
      {record.slug && !record.missing ? <Link to={`/${record.slug}/${record.id}`}>{label}</Link> : <span>{label}</span>}
      <span className="small-muted"> · {record.section}</span>
      {record.missing ? <span className="small-muted"> (missing)</span> : null}
      {record.status ? <StatusBadge value={record.status} /> : null}
    </span>
  );
};

const RecordLinksSection = ({ slug, id, canUpdateContent = false }) => {
  const [links, setLinks] = useState(null);
  const [types, setTypes] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [why, setWhy] = useState(null);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [relation, setRelation] = useState('');
  const [targetSlug, setTargetSlug] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [targetId, setTargetId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setError('');
    Promise.all([
      http.get(`/links/${encodeURIComponent(slug)}/${encodeURIComponent(id)}`),
      http.get('/edges/types'),
      http.get('/foundations/graph'),
      http.get(`/why/${encodeURIComponent(slug)}/${encodeURIComponent(id)}`).catch(() => ({ data: null })),
    ])
      .then(([linksResponse, typesResponse, graphResponse, whyResponse]) => {
        if (!active) return;
        setLinks(linksResponse.data);
        setTypes(Array.isArray(typesResponse.data?.types) ? typesResponse.data.types : []);
        setNodes(Array.isArray(graphResponse.data?.nodes) ? graphResponse.data.nodes : []);
        setWhy(whyResponse.data);
      })
      .catch((requestError) => {
        if (active) setError(messageOf(requestError, 'Unable to load links.'));
      });
    return () => { active = false; };
  }, [slug, id, reloadKey]);

  const section = links?.section;
  const options = useMemo(() => (section ? relationOptions(types, section) : []), [types, section]);
  const selectedOption = options.find((option) => option.value === relation) || null;
  const targetNodes = useMemo(() => nodes
    .filter((node) => node.slug && (!selectedOption?.targets || selectedOption.targets.includes(node.section)))
    .sort((a, b) => String(a.section).localeCompare(String(b.section))), [nodes, selectedOption]);
  const targetNode = nodes.find((node) => node.slug === targetSlug) || null;

  useEffect(() => {
    let active = true;
    setCandidates([]);
    setTargetId('');
    if (!targetSlug) return undefined;
    http.get(`/${encodeURIComponent(targetSlug)}`, { params: { limit: 100, page: 1, sort: 'updatedAt', order: 'DESC' } })
      .then(({ data }) => {
        if (active) setCandidates(Array.isArray(data?.data) ? data.data : []);
      })
      .catch((requestError) => {
        if (active) setError(messageOf(requestError, 'Unable to load records to link.'));
      });
    return () => { active = false; };
  }, [targetSlug]);

  const refresh = useCallback(() => setReloadKey((key) => key + 1), []);

  async function addLink(event) {
    event.preventDefault();
    if (!relation || !targetSlug || !targetId) return;
    setSaving(true);
    setError('');
    try {
      await http.post('/links', { from: { model: slug, id }, to: { model: targetSlug, id: targetId }, type: relation });
      setTargetId('');
      refresh();
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to add link.'));
    } finally {
      setSaving(false);
    }
  }

  async function removeLink(edgeId) {
    setError('');
    try {
      await http.delete(`/links/${encodeURIComponent(edgeId)}`);
      refresh();
    } catch (requestError) {
      setError(messageOf(requestError, 'Unable to remove link.'));
    }
  }

  const slugOf = (sectionName) => nodes.find((node) => node.section === sectionName)?.slug || null;
  const decisionSlug = slugOf('Decision');
  const assumptionSlug = slugOf('Assumption');
  const groups = Object.entries(links?.groups || {});
  const decisions = why?.decisions || [];

  return (
    <RecordSection title="Links & decisions" className="record-links">
      {error ? <p className="error-text" role="alert">{error}</p> : null}
      {!links && !error ? <p>Loading links...</p> : null}

      {links ? (
        groups.length ? (
          <div className="record-links-groups">
            {groups.map(([groupRelation, items]) => (
              <div key={groupRelation} className="record-links-group">
                <span className="kv-label">{items[0]?.label || humanize(groupRelation)}</span>
                <ul>
                  {items.map((item) => (
                    <li key={item.id}>
                      <RecordRef record={item.record} />
                      {canUpdateContent ? (
                        <button className="button-secondary record-links-remove" type="button" onClick={() => removeLink(item.id)}>
                          Remove
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ) : <p className="small-muted">No typed links yet.</p>
      ) : null}

      {links && canUpdateContent ? (
        <form className="record-links-form" onSubmit={addLink}>
          <select value={relation} onChange={(event) => { setRelation(event.target.value); setTargetSlug(''); }} aria-label="Relation">
            <option value="">Relation…</option>
            {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <select value={targetSlug} onChange={(event) => setTargetSlug(event.target.value)} disabled={!relation} aria-label="Target model">
            <option value="">Model…</option>
            {targetNodes.map((node) => <option key={node.section} value={node.slug}>{node.section}</option>)}
          </select>
          <select value={targetId} onChange={(event) => setTargetId(event.target.value)} disabled={!targetSlug} aria-label="Target record">
            <option value="">Record…</option>
            {candidates
              .filter((row) => !(targetSlug === slug && String(row.id) === String(id)))
              .map((row) => (
                <option key={row.id} value={row.id}>{(targetNode?.handle && row[targetNode.handle]) || row.title || row.name || row.id}</option>
              ))}
          </select>
          <button className="button-primary" type="submit" disabled={saving || !targetId}>
            {saving ? 'Linking...' : 'Add link'}
          </button>
        </form>
      ) : null}

      {decisions.length ? (
        <div className="record-links-why">
          <h3 className="section-title">Why is it like this?</h3>
          <ul>
            {decisions.map((decision) => (
              <li key={decision.id}>
                {decisionSlug ? <Link to={`/${decisionSlug}/${decision.id}`}>{decision.title || 'Decision'}</Link> : decision.title || 'Decision'}{' '}
                <StatusBadge value={decision.status} />
                {decision.chosenOption ? <span className="small-muted"> — chose {decision.chosenOption}</span> : null}
                {decision.assumptions?.length ? (
                  <ul>
                    {decision.assumptions.map((assumption) => (
                      <li key={assumption.id}>
                        Assumes: {assumptionSlug ? <Link to={`/${assumptionSlug}/${assumption.id}`}>{assumption.statement}</Link> : assumption.statement}{' '}
                        <StatusBadge value={assumption.status} />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
          {why?.atRisk ? (
            <p className="record-links-warning">
              {why.atRisk} supporting assumption{why.atRisk === 1 ? ' is' : 's are'} at risk or invalidated.
            </p>
          ) : null}
        </div>
      ) : null}
    </RecordSection>
  );
};

export default RecordLinksSection;
